import React from "react";
import { graphql, useFragment } from "react-relay";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tippy from "@tippyjs/react";
import cx from "classnames";
import { EditorView } from "prosemirror-view";

import { showFeatureModal } from "~/billing";
import { billingUpgradeIconDef } from "~/billing/utils";
import type {
	ActionItemIntegration,
	BaseIntegrationDetails,
	isActionItemIntegratedArgument,
} from "~/integrations/actionItemIntegration";
import { AsanaBlock, AsanaStreamSettingsIntegrationCard } from "~/integrations/asana/AsanaStreamSettings";
import { ReactComponent as AsanaLogo } from "~/legacy-assets/images/logos/asana.svg";
import { promptAsanaAuth } from "~/lib/auth";
import AsanaItems, { AsanaItemsPluginKey } from "~/lib/prose/plugins/asanaItems";
import { getContext } from "~/lib/prose/plugins/context";
import type { TooltipProps } from "~/lib/utils";
import { AsanaPerItemModal, type AsanaPerItemModalProps } from "~/settings/Integrations/Asana/AsanaIntegration";
import type { IntegrationConfigProps, IntegrationDetailsType } from "~/stream/StreamSettings/ActionItemIntegrations";
import { __globalModalContext } from "~/ui/modals";

import { displayActionItemUnlinkingModal } from "../actionItemIntegration/actionItemUnlinkingModal";

import type { asanaStreamDetails$key } from "~/__generated__/asanaStreamDetails.graphql";

import styles from "~/notes/css/assignableDropdown.module.css";

import actionItemIntegrationStyles from "../actionItemIntegration/css/styles.module.css";

const asanaStreamDetails = graphql`
	fragment asanaStreamDetails on AsanaStreamNode {
		asanaProject {
			name
			type
			externalId
			workspaceExternalId
		}
		createdBy {
			fullName
		}
		includeAiGenerated
	}
`;

export interface AsanaStreamIntegrationDetails extends BaseIntegrationDetails {
	projectType: string | null;
	projectExternalId: string;
	workspaceExternalId?: string;
	name: string;
}

function useAsanaStreamIntegrationDetails(integrationOrAsanaStream: IntegrationDetailsType) {
	if (!("asanaStream" in integrationOrAsanaStream) || !integrationOrAsanaStream.asanaStream) {
		throw new Error("AsanaStream details do not exist on the connected integration details"); // Should never encounter this
	}

	const streamDetails = useFragment<asanaStreamDetails$key>(asanaStreamDetails, integrationOrAsanaStream.asanaStream);

	return {
		containerTitle: streamDetails?.asanaProject.name ?? "",
		createdBy: streamDetails?.createdBy.fullName ?? "",
		includeAiGenerated: streamDetails?.includeAiGenerated ?? true,
		projectType: streamDetails?.asanaProject.type ?? null,
		projectExternalId: streamDetails?.asanaProject.externalId ?? undefined,
		workspaceExternalId: streamDetails?.asanaProject.workspaceExternalId ?? undefined,
		name: streamDetails?.asanaProject.name ?? "",
	}; //Should never render empty strings
}

export const asanaKey = "ASANA";

export const asana: ActionItemIntegration = {
	key: asanaKey,
	logo: <AsanaLogo className={actionItemIntegrationStyles.integrationLogo} />,
	billingFeatureName: "asana_integration",
	externalActionItemName: "task",
	externalContainerType: "project",
	displayName: "Asana",
	isActionItemIntegrated: isActionItemIntegratedWithAsana,
	getDropdownMenuItem: getAsanaMenuItem,
	promptAuth: promptAsanaAuth,
	streamSettingsCard: AsanaStreamSettingsIntegrationCard,
	configurationPane: getAsanaConfigurationPane,
	useIntegrationDetails: useAsanaStreamIntegrationDetails,
};

export default asana;

function getAsanaConfigurationPane({
	integration,
	stream,
	viewer,
	onFinish,
	onCancel,
	integrationDetails,
}: IntegrationConfigProps) {
	if (!viewer) return <></>;
	return (
		<AsanaBlock
			viewer={viewer}
			stream={stream}
			onConfigFinish={onFinish}
			integration={integration}
			onCancel={onCancel}
			integrationDetails={integrationDetails as AsanaStreamIntegrationDetails}
		/>
	);
}

function isActionItemIntegratedWithAsana({ state, blockKey }: isActionItemIntegratedArgument) {
	return !!AsanaItemsPluginKey.getState(state)?.itemsData[blockKey];
}

export const displayAsanaActionItemLinkingModal = async ({
	userId,
	blockKey,
	noteId,
	isAsanaConnected,
	initialText,
}: AsanaPerItemModalProps) => {
	const result = await __globalModalContext?.open<boolean>(({ close }) => (
		<AsanaPerItemModal
			close={close}
			userId={userId}
			blockKey={blockKey}
			noteId={noteId}
			isAsanaConnected={isAsanaConnected}
			initialText={initialText}
		/>
	));
	return result;
};

function getAsanaMenuItem({
	view,
	blockKey,
	noteId,
	hasAccessToAsana,
	close,
	disabled,
	tooltip,
}: {
	view: EditorView;
	blockKey: string;
	noteId: string;
	hasAccessToAsana: boolean;
	close: () => void;
	disabled: boolean;
	tooltip?: TooltipProps;
}) {
	const ext = view.extensions.get("asana-items") as AsanaItems;
	const currentBlockAsanaInfo = AsanaItemsPluginKey.getState(view.state)?.itemsData[blockKey];
	const isAsanaConnected = getContext(view.state).asanaIsConnected ?? false;
	const userId = getContext(view.state).userId;

	const dontAllowUnlink = (currentBlockAsanaInfo && !currentBlockAsanaInfo?.asanablocklink?.externalId) || false;

	const closeMenu = close;

	return {
		id: "addAsana",
		disabled: dontAllowUnlink || disabled,
		toolTip: tooltip,
		action: () => {
			if (!ext) return true;

			// User who does not have access to Asana can still unlink from Asana
			// ("Unlink" is the only option available if currentBlockAsanaInfo exists)
			if (currentBlockAsanaInfo) {
				displayActionItemUnlinkingModal({
					integration: asana,
					userId: userId,
					blockKey: blockKey,
					noteId: noteId,
				});
			} else if (hasAccessToAsana) {
				displayAsanaActionItemLinkingModal({
					userId: userId,
					blockKey: blockKey,
					noteId: noteId,
					isAsanaConnected: isAsanaConnected,
				});
			} else {
				showFeatureModal("asana_integration");
			}
			closeMenu();
			return true;
		},
		left: currentBlockAsanaInfo ? (
			<AsanaLogo
				className={cx(actionItemIntegrationStyles.integrationLogo, {
					[actionItemIntegrationStyles.greyscale]: dontAllowUnlink,
				})}
			/>
		) : (
			<AsanaLogo className={actionItemIntegrationStyles.integrationLogo} />
		),
		right: (() => {
			return currentBlockAsanaInfo ? (
				<Tippy content="Cannot unlink while syncing" disabled={!dontAllowUnlink}>
					<div contentEditable={false}>Unlink from Asana…</div>
				</Tippy>
			) : (
				<div contentEditable={false} className={styles.menuItemWithCrown}>
					<span>Link to Asana…</span>
					{!hasAccessToAsana && <FontAwesomeIcon icon={billingUpgradeIconDef} />}
				</div>
			);
		})(),
	};
}
